import type { UiCorners, UiNumberKey, UiSize } from '@mogzo/shared';
import { type ReactNode, useId, useState } from 'react';
import { limits, NUMBER_SPECS } from './model';

/** The Game UI editor's controls: a page builder's settings rows (M19). */

const UNIT_WORDS = { px: 'pixels', '%': 'percent', s: 'seconds' } as const;

export function DeviceIcon({ size, px = 18 }: { size: UiSize; px?: number }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={px < 16 ? 2.2 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {size === 'computer' && (
        <>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </>
      )}
      {size === 'tablet' && (
        <>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <path d="M12 18h.01" />
        </>
      )}
      {size === 'phone' && (
        <>
          <rect x="7" y="2" width="10" height="20" rx="2" />
          <path d="M12 18h.01" />
        </>
      )}
    </svg>
  );
}

/** Where a setting's value comes from, as the label row shows it. */
export interface Source {
  /** Set in the view being edited. */
  own: boolean;
  /** "This view", "MOGZO default" or "Theme · Computer · Game". */
  text: string;
  /** The device icon's longer explanation. */
  title: string;
}

/** A setting's label row: the device icon, where the value comes from and a reset when it's set here. */
function SettingRow({
  labelId,
  label,
  size,
  source,
  onReset,
  children,
  help,
  warn,
}: {
  labelId: string;
  label: string;
  size: UiSize;
  source: Source;
  onReset: () => void;
  children: ReactNode;
  help?: string;
  warn?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 px-4 pt-1.5 pb-2">
      <div className="flex min-h-[22px] items-center gap-1.5">
        <span id={labelId} className="text-[12.5px] font-medium text-[#3a3550]">
          {label}
        </span>
        <span
          title={source.title}
          className={`grid size-[18px] flex-none place-items-center rounded ${
            source.own ? 'bg-[#6d4aff] text-white' : 'text-[#a7a1c2]'
          }`}
        >
          <DeviceIcon size={size} px={12} />
        </span>
        <span
          className={`min-w-0 flex-1 truncate text-right text-[11px] ${
            source.own ? 'text-[#4a2fb8]' : 'text-[#8a859c]'
          }`}
        >
          {source.text}
        </span>
        {source.own && (
          <button
            type="button"
            aria-label={`Reset ${label.toLowerCase()}`}
            title="Reset: use the value this view would take otherwise"
            onClick={onReset}
            className="grid size-[22px] flex-none place-items-center rounded bg-[#f3f0ff] text-[#4a2fb8]"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
        )}
      </div>
      {children}
      {help && <p className="text-[11.5px] leading-snug text-[#8a859c]">{help}</p>}
      {warn && (
        <p role="alert" className="text-[11.5px] leading-snug text-[#b42318]">
          {warn}
        </p>
      )}
    </div>
  );
}

/** A number: a slider for the usual range, and a box that takes anything within the limits. */
export function NumberSetting({
  name,
  value,
  size,
  source,
  onChange,
}: {
  name: UiNumberKey;
  value: number;
  size: UiSize;
  source: Source;
  onChange: (value: number | undefined) => void;
}) {
  const labelId = useId();
  const spec = NUMBER_SPECS[name];
  const range = limits(name);
  const [draft, setDraft] = useState<string | null>(null);
  const [warn, setWarn] = useState('');
  const commit = (n: number, warning = '') => {
    setDraft(null);
    setWarn(warning);
    onChange(n);
  };
  const unit = ` ${range.unit}`;
  const tooSmall = spec.minMsg ?? `At least ${range.hardMin}${unit}.`;

  return (
    <SettingRow
      labelId={labelId}
      label={spec.label}
      size={size}
      source={source}
      onReset={() => {
        setDraft(null);
        setWarn('');
        onChange(undefined);
      }}
      help={spec.help}
      warn={warn}
    >
      <div className="flex items-center gap-3">
        <input
          type="range"
          aria-labelledby={labelId}
          min={range.min}
          max={range.max}
          step={1}
          value={Math.min(Math.max(value, range.min), range.max)}
          onChange={(event) => commit(Number(event.target.value))}
          className="h-1 min-w-0 flex-1 accent-[#6d4aff]"
        />
        <span
          className={`flex h-7 flex-none items-stretch overflow-hidden rounded border bg-white ${
            warn ? 'border-[#d92d20]' : 'border-[#d9d7e2]'
          }`}
        >
          <input
            type="number"
            aria-label={`${spec.label}, in ${UNIT_WORDS[range.unit]}`}
            min={range.hardMin}
            max={range.hardMax}
            value={draft ?? String(value)}
            onChange={(event) => {
              const raw = event.target.value;
              const n = Number.parseInt(raw, 10);
              if (Number.isNaN(n) || n < range.hardMin) {
                setDraft(raw);
                setWarn(Number.isNaN(n) ? '' : tooSmall);
              } else if (n > range.hardMax) {
                commit(range.hardMax, `At most ${range.hardMax}${unit}, so it was set to that.`);
              } else {
                commit(n);
              }
            }}
            onBlur={() => {
              if (draft === null) return;
              const n = Number.parseInt(draft, 10);
              if (Number.isNaN(n)) {
                setDraft(null);
                setWarn('');
              } else if (n < range.hardMin) {
                commit(range.hardMin, `${tooSmall} Set to ${range.hardMin}${unit}.`);
              } else {
                commit(n);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            className={`w-[50px] border-0 bg-transparent pr-0.5 pl-2 font-mono text-xs ${
              source.own ? 'text-ink' : 'text-[#8a859c]'
            }`}
          />
          <span className="grid place-items-center border-l border-[#ececf1] bg-[#f7f6fa] px-[7px] text-[11px] text-[#8a859c]">
            {range.unit}
          </span>
        </span>
      </div>
    </SettingRow>
  );
}

/** Two or three choices side by side. */
export function ChoiceSetting<T extends string>({
  label,
  value,
  options,
  size,
  source,
  onChange,
}: {
  label: string;
  value: T;
  options: [T, string][];
  size: UiSize;
  source: Source;
  onChange: (value: T | undefined) => void;
}) {
  const labelId = useId();
  return (
    <SettingRow
      labelId={labelId}
      label={label}
      size={size}
      source={source}
      onReset={() => onChange(undefined)}
    >
      <fieldset
        aria-labelledby={labelId}
        className="m-0 flex min-w-0 overflow-hidden rounded border border-[#d9d7e2] p-0"
      >
        {options.map(([option, text], i) => {
          const on = option === value;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(option)}
              className={`h-7 flex-1 px-2 text-xs font-semibold ${i ? 'border-l border-[#d9d7e2]' : ''} ${
                on
                  ? source.own
                    ? 'bg-[#6d4aff] text-white'
                    : 'bg-[#4a4463] text-white'
                  : 'bg-white text-[#3a3550]'
              }`}
            >
              {text}
            </button>
          );
        })}
      </fieldset>
    </SettingRow>
  );
}

const CORNERS: [keyof UiCorners, string, string][] = [
  ['tl', 'Top L', 'Top left'],
  ['tr', 'Top R', 'Top right'],
  ['br', 'Bot R', 'Bottom right'],
  ['bl', 'Bot L', 'Bottom left'],
];

/** Four corners, linked (one value sets all four) or each on its own. */
export function CornersSetting({
  value,
  size,
  source,
  linked,
  onLink,
  squareBottom,
  help,
  onChange,
}: {
  value: UiCorners;
  size: UiSize;
  source: Source;
  linked: boolean;
  onLink: (linked: boolean) => void;
  /** A phone's sheet: its bottom corners stay square. */
  squareBottom: boolean;
  help?: string;
  onChange: (value: UiCorners | undefined) => void;
}) {
  const labelId = useId();
  const linkLabel = linked
    ? 'Linked: one value sets all four corners. Click to set each on its own.'
    : 'Each corner on its own. Click to link all four.';
  return (
    <SettingRow
      labelId={labelId}
      label="Corners"
      size={size}
      source={source}
      onReset={() => onChange(undefined)}
      help={help}
    >
      <fieldset
        aria-labelledby={labelId}
        className="m-0 flex min-w-0 items-start gap-1 border-0 p-0"
      >
        {CORNERS.map(([key, short, full]) => {
          const locked = squareBottom && (key === 'bl' || key === 'br');
          return (
            <label
              key={key}
              title={locked ? `${full}: stays square on a phone` : full}
              className="flex min-w-0 flex-1 flex-col items-center gap-1 text-[9.5px] font-semibold tracking-[0.04em] text-[#8a859c] uppercase"
            >
              <input
                type="number"
                aria-label={`${full} corner, in pixels`}
                min={0}
                max={64}
                value={locked ? 0 : value[key]}
                disabled={locked}
                onChange={(event) => {
                  const n = Number.parseInt(event.target.value, 10);
                  if (Number.isNaN(n)) return;
                  const v = Math.max(0, Math.min(64, n));
                  onChange(linked ? { tl: v, tr: v, br: v, bl: v } : { ...value, [key]: v });
                }}
                className={`h-[30px] w-full rounded border border-[#d9d7e2] px-0.5 text-center font-mono text-xs ${
                  locked
                    ? 'bg-[#f3f2f7] text-[#b9b3cf]'
                    : source.own
                      ? 'bg-white text-ink'
                      : 'bg-white text-[#8a859c]'
                }`}
              />
              {short}
            </label>
          );
        })}
        <button
          type="button"
          aria-pressed={linked}
          aria-label={linkLabel}
          title={linkLabel}
          onClick={() => onLink(!linked)}
          className={`grid size-[30px] flex-none place-items-center rounded border ${
            linked
              ? 'border-[#6d4aff] bg-[#6d4aff] text-white'
              : 'border-[#d9d7e2] bg-white text-[#8a859c]'
          }`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
      </fieldset>
    </SettingRow>
  );
}
